import React, { useId } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Pagination from '@mui/material/Pagination'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { rangeLabel } from '../../helpers/pagination'
import { PAGINATION, TEXT, menuProps } from '../../theme/tokens'

/**
 * The paging bar under a list: rows (or cards) per page, the range shown
 * ("61-120 of 3,412", or "of more than 120" while the total is unknown),
 * Previous and Next, and, when every page can be opened directly (the whole
 * list is known), the page numbers so a user can jump to page 7.
 *
 * Shared by the Pipelines list (cursor pages: no page numbers, since page 7
 * cannot be read without pages 1 to 6) and the Templates library. Pages
 * count from 0 here; `onPageChange` gets the page to show. On a phone the
 * bar wraps onto two lines.
 */
const ListPagination = ({
    label,
    page,
    pageSize,
    pageSizeOptions,
    onPageChange,
    onPageSizeChange,
    from,
    to,
    total = null,
    hasNext,
    pageCount,
    disabled = false,
    sizeLabel = 'Rows per page',
}) => {
    const sizeLabelId = useId()
    const go = (next) => {
        if (next !== page) onPageChange(next)
    }
    return (
        <Box
            component="nav"
            aria-label={label}
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                columnGap: 2,
                rowGap: 1.5,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.25,
                }}
            >
                <Typography
                    id={sizeLabelId}
                    variant="body2"
                    sx={{ color: TEXT.muted }}
                >
                    {sizeLabel}
                </Typography>
                <Select
                    size="small"
                    labelId={sizeLabelId}
                    value={pageSize}
                    disabled={disabled}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    MenuProps={menuProps}
                    sx={{ minWidth: 76 }}
                >
                    {pageSizeOptions.map((size) => (
                        <MenuItem key={size} value={size}>
                            {size}
                        </MenuItem>
                    ))}
                </Select>
                <Typography
                    variant="body2"
                    aria-live="polite"
                    data-testid="pagination-range"
                    sx={{ color: TEXT.secondary, whiteSpace: 'nowrap' }}
                >
                    {rangeLabel({ from, to, total })}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                    size="small"
                    aria-label="Previous page"
                    disabled={disabled || page === 0}
                    onClick={() => go(page - 1)}
                    sx={{ color: PAGINATION.text }}
                >
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
                {pageCount > 1 && (
                    <Pagination
                        aria-label="Page numbers"
                        count={pageCount}
                        page={page + 1}
                        onChange={(_, number) => go(number - 1)}
                        siblingCount={1}
                        boundaryCount={1}
                        hidePrevButton
                        hideNextButton
                        size="small"
                        disabled={disabled}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: PAGINATION.text,
                            },
                            '& .MuiPaginationItem-root.Mui-selected, & .MuiPaginationItem-root.Mui-selected:hover':
                                {
                                    color: PAGINATION.selectedText,
                                    backgroundColor: PAGINATION.selectedBg,
                                    border: `1px solid ${PAGINATION.selectedBorder}`,
                                },
                        }}
                    />
                )}
                <IconButton
                    size="small"
                    aria-label="Next page"
                    disabled={disabled || !hasNext}
                    onClick={() => go(page + 1)}
                    sx={{ color: PAGINATION.text }}
                >
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    )
}

ListPagination.propTypes = {
    /** The bar's accessible name, e.g. "Pipelines pages". */
    label: PropTypes.string.isRequired,
    /** The page shown, from 0. */
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
    onPageChange: PropTypes.func.isRequired,
    onPageSizeChange: PropTypes.func.isRequired,
    /** 1-based first and last row shown (to < from when none). */
    from: PropTypes.number.isRequired,
    to: PropTypes.number.isRequired,
    /** The whole list's size, or null while unknown. */
    total: PropTypes.number,
    hasNext: PropTypes.bool.isRequired,
    /** Pass only when any page can be opened directly: shows page numbers. */
    pageCount: PropTypes.number,
    /** While a page loads: every control is off. */
    disabled: PropTypes.bool,
    sizeLabel: PropTypes.string,
}

export default ListPagination
